import { VersionSelectMenu } from "@features/versions/components/VersionSelectMenu";
import { useUpdateSessionVersion } from "@features/versions/hooks/useVersions";
import type { SchematicVersion } from "@models/project";
import { Tooltip } from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useProjectStore } from "@stores/projectStore";

/**
 * Lets the user re-point the conversation at a different schematic
 * version, right next to the scope chip above the composer. The agent
 * resolves the version per tool call from the session's
 * `active_version_id`, so switching here changes which version every
 * subsequent query reads — ask about v3, switch, ask about v2.
 *
 * Two states, both handled by writing the project store first:
 *   - live session → also re-pins it server-side via
 *     `updateVersion` so the in-flight conversation moves over.
 *   - no session yet → the project-store pin is read by
 *     `sessionsApi.create({ version_id })`, so the next message opens
 *     a session already pinned to the chosen version.
 *
 * Renders nothing on single-version projects (VersionSelectMenu bails
 * when there's only one version) — no clutter where there's no choice.
 */
export function ChatVersionSwitcher() {
  const sessionId = useChatStore((s) => s.sessionId);
  const activeProjectId = useProjectStore((s) => s.activeProject);
  const activeVersionId = useProjectStore((s) => s.activeVersionId);
  const setActiveVersion = useProjectStore((s) => s.setActiveVersion);
  const updateSessionVersion = useUpdateSessionVersion();

  if (!activeProjectId) return null;

  const handleSelect = (v: SchematicVersion) => {
    // Optimistic local pin: updates the chip label immediately and
    // feeds session creation if no session exists yet.
    setActiveVersion(v.id, v.version_number);
    if (sessionId) {
      updateSessionVersion.mutate({ sessionId, versionId: v.id });
    }
  };

  return (
    <Tooltip title="Schematic version the assistant reads — switch to query a different version">
      <span>
        <VersionSelectMenu
          projectId={activeProjectId}
          activeVersionId={activeVersionId}
          onSelect={handleSelect}
          disabled={updateSessionVersion.isPending}
        />
      </span>
    </Tooltip>
  );
}
